import { auth, db, googleProvider } from '@/firebase-config';
import { logger } from '@/utils/logger';
import {
  createUserWithEmailAndPassword,
  linkWithPopup,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';

import type { AuthSession, IdentityClient, Unsubscribe } from '../contracts';

type FirebaseUserLike = {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
  getIdToken(forceRefresh?: boolean): Promise<string>;
};

type StoredUserProfile = {
  email?: unknown;
  displayName?: unknown;
  photoURL?: unknown;
  canonicalUserId?: unknown;
  isSuperAdmin?: unknown;
  activeOrgId?: unknown;
  needsSetup?: unknown;
  projectWelcomeDismissals?: unknown;
};

const isStoredUserProfile = (value: unknown): value is StoredUserProfile =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

function booleanRecord(value: unknown): Record<string, boolean> | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return;
  }

  const entries = Object.entries(value as Record<string, unknown>);
  if (!entries.every(([, entryValue]) => typeof entryValue === 'boolean')) {
    return;
  }
  return Object.fromEntries(entries) as Record<string, boolean>;
}

function basicSession(user: FirebaseUserLike): AuthSession {
  const authEmail = user.email ?? '';
  return {
    userId: user.uid,
    authUid: user.uid,
    canonicalUserId: user.uid,
    authEmail,
    email: authEmail,
    displayName: user.displayName || authEmail.split('@')[0] || 'User',
    photoURL: user.photoURL ?? undefined,
  };
}

function mergeStoredSession(
  base: AuthSession,
  profileData: StoredUserProfile,
  userId: string,
  authUid: string,
  canonicalUserId: string,
): AuthSession {
  return {
    ...base,
    userId,
    authUid,
    canonicalUserId,
    email: nonEmptyString(profileData.email) ?? base.email,
    displayName: nonEmptyString(profileData.displayName) ?? base.displayName,
    photoURL: nonEmptyString(profileData.photoURL) ?? base.photoURL,
    isSuperAdmin: profileData.isSuperAdmin === true,
    activeOrgId: typeof profileData.activeOrgId === 'string' ? profileData.activeOrgId : undefined,
    needsSetup: profileData.needsSetup === true,
    projectWelcomeDismissals: booleanRecord(profileData.projectWelcomeDismissals),
  };
}

export class FirebaseIdentityClient implements IdentityClient {
  private currentSession: AuthSession | null = null;
  private listeners = new Set<(session: AuthSession | null) => void>();
  private profileLoadSequence = 0;

  subscribe(listener: (session: AuthSession | null) => void): Unsubscribe {
    let active = true;
    this.listeners.add(listener);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const sequence = ++this.profileLoadSequence;
      void this.publishAuthUser(user as FirebaseUserLike | null, sequence, () => active);
    });

    return () => {
      active = false;
      this.listeners.delete(listener);
      unsubscribe();
    };
  }

  getCurrentSession(): AuthSession | null {
    return this.currentSession;
  }

  async getSession(): Promise<AuthSession | null> {
    if (this.currentSession) return this.currentSession;
    return this.refreshSession();
  }

  async refreshSession(): Promise<AuthSession | null> {
    const sequence = ++this.profileLoadSequence;
    const user = auth.currentUser as FirebaseUserLike | null | undefined;
    return this.publishAuthUser(user ?? null, sequence, () => true);
  }

  getAuthUid(): string | undefined {
    return (auth.currentUser as FirebaseUserLike | null | undefined)?.uid || undefined;
  }

  async getToken(options: { forceRefresh?: boolean } = {}): Promise<string | null> {
    const user = auth.currentUser as FirebaseUserLike | null | undefined;
    return user ? user.getIdToken(options.forceRefresh === true) : null;
  }

  async signInWithGoogle(): Promise<AuthSession | null> {
    const result = await signInWithPopup(auth, googleProvider);
    await this.touchUserProfileAfterSignIn(result.user as FirebaseUserLike);
    return this.refreshSession();
  }

  async signInWithEmail(email: string, password: string): Promise<AuthSession | null> {
    const result = await signInWithEmailAndPassword(auth, email, password);
    await this.touchUserProfileAfterSignIn(result.user as FirebaseUserLike);
    return this.refreshSession();
  }

  async signUpWithEmail(email: string, password: string, displayName: string): Promise<AuthSession | null> {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(result.user, { displayName });
    }
    await this.touchUserProfileAfterSignIn(result.user as FirebaseUserLike);
    return this.refreshSession();
  }

  async linkGoogleProvider(): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('Authentication required');
    await linkWithPopup(user, googleProvider);
  }

  async signInWithGoogleForAuthorizationLink(): Promise<AuthSession | null> {
    await signInWithPopup(auth, googleProvider);
    return this.refreshSession();
  }

  async signOut(): Promise<void> {
    ++this.profileLoadSequence;
    await firebaseSignOut(auth);
    this.currentSession = null;
    this.emit(null);
  }

  private async publishAuthUser(
    user: FirebaseUserLike | null,
    sequence: number,
    isActive: () => boolean,
  ): Promise<AuthSession | null> {
    if (!user) {
      if (!isActive() || sequence !== this.profileLoadSequence) return this.currentSession;
      this.currentSession = null;
      this.emit(null);
      return null;
    }

    const session = await this.loadSessionForAuthUser(user);
    if (!isActive() || sequence !== this.profileLoadSequence) return this.currentSession;
    this.currentSession = session;
    this.emit(session);
    return session;
  }

  private emit(session: AuthSession | null): void {
    for (const listener of this.listeners) {
      listener(session);
    }
  }

  private async loadSessionForAuthUser(user: FirebaseUserLike): Promise<AuthSession> {
    const base = basicSession(user);
    const authUid = user.uid;

    try {
      const authSnap = await getDoc(doc(db, 'users', authUid));
      const authData = authSnap.exists() ? authSnap.data() as unknown : {};
      const authProfile = isStoredUserProfile(authData) ? authData : {};
      const canonicalUserId = nonEmptyString(authProfile.canonicalUserId);

      if (canonicalUserId && canonicalUserId !== authUid) {
        const canonicalSnap = await getDoc(doc(db, 'users', canonicalUserId));
        const canonicalData = canonicalSnap.exists() ? canonicalSnap.data() as unknown : {};
        const canonicalProfile = isStoredUserProfile(canonicalData) ? canonicalData : {};
        return mergeStoredSession(base, canonicalProfile, canonicalUserId, authUid, canonicalUserId);
      }

      return mergeStoredSession(base, authProfile, authUid, authUid, authUid);
    } catch (error: unknown) {
      logger.error('Failed to load user profile from Firestore:', error);
      return base;
    }
  }

  private async touchUserProfile(user: FirebaseUserLike): Promise<void> {
    const session = basicSession(user);
    await setDoc(doc(db, 'users', user.uid), {
      email: session.email,
      displayName: session.displayName,
      photoURL: session.photoURL || null,
      lastLogin: serverTimestamp(),
    }, { merge: true });
  }

  private async touchUserProfileAfterSignIn(user: FirebaseUserLike): Promise<void> {
    try {
      await this.touchUserProfile(user);
    } catch (error: unknown) {
      logger.error('Profile update failed, but auth succeeded:', error);
    }
  }
}
